import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, inject, Injectable, signal, TemplateRef, viewChild, DOCUMENT } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Observable, catchError, combineLatest, of } from 'rxjs';
import { MatDialog, MatDialogClose } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { backend, domain } from '../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { BucketPipe } from '../../bucket.pipe';
import { ImageFallbackDirective } from '../../image-fallback.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'game-invite',
  templateUrl: './game-invite.component.html',
  styleUrls: ['./game-invite.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, RouterLink, BucketPipe, ImageFallbackDirective, MatDialogClose],
  host: {
    '[style.--game-background-color]': 'elementVariables().background_color',
    '[style.--game-text-color]': 'elementVariables().text_color',
    '[style.--button-background-color]': 'elementVariables().button_background_color',
    '[style.--button-text-color]': 'elementVariables().button_text_color',
    '[style.--code-text-color]': 'elementVariables().code_text_color',
  }
})

export default class GameInviteComponent {
  activated = inject(ActivatedRoute);
  isLoading = signal<boolean>(true);
  elements = inject(ElementsService);
  dialogs = inject(MatDialog);
  toast = inject(ToastrService);
  destroy = inject(DestroyRef);
  document = inject(DOCUMENT);

  elementVariables = signal<{ [key: string]: any }>({
    'background_color': '#FFFFFF',
    'button_background_color': '#0075FF',
    "button_text_color": "#FFFFFF",
    "code_text_color": "#0075FF",
    "text_color": "#181921"
  });

  queryParams: { [key: string]: string } = {
    'path': encodeURI(this.elementVariables().route?.app_path ?? ""),
    'id': encodeURI(this.elementVariables().route?.app_id ?? ""),
    'url': encodeURI(domain) +'%2Fget-the-app',
    'utm_campaign':'coca_cola_game_2023_landing',
    'utm_source': 'cloudigo',
    'utm_medium':'web',
    'store':'1',
  };

  virtualCode = signal<string>('');
  howToPopup = viewChild<TemplateRef<any>>('howToImg');

  copyAndGo(text: string){
    if(text){ 

      if(!navigator.clipboard){
        let codeBox = this.document.createElement('textarea');
        codeBox.style.position = 'fixed';
        codeBox.style.height = '0';
        codeBox.value = text;
        this.document.body.appendChild(codeBox);
        codeBox.focus();
        codeBox.select();
        this.document.execCommand('copy');
        this.document.body.removeChild(codeBox);
      } else {
        navigator.clipboard.writeText(text);
      }
    }
  }

  openHowTo(){
    const howToPopup = this.howToPopup();
    if(howToPopup){
      this.dialogs.open(howToPopup, {
        ariaLabel: 'How to Play',
        ariaModal: true,
        minWidth: 150,
        maxWidth: 500
      });

      return true;
    }

    return false;
  }

  constructor() {

    const referral_tracking_name: string = this.activated.snapshot.params.path;
    const referral_code: string = this.activated.snapshot.queryParams.referral_code;
    
    afterNextRender(() => {
      try {
        this.virtualCode.set(localStorage?.getItem(`${referral_tracking_name}_${referral_code}`)??'');
      }catch(e){}

      combineLatest([
        this.elements.getElementVariables('/invite/' + referral_tracking_name),
        ...referral_code && !this.virtualCode ? [
          this.elements.getReferralCode(referral_tracking_name, referral_code).pipe(catchError(e => of({ code: null })))
        ] : [
          of({ code: null })
        ]
      ]).pipe(takeUntilDestroyed(this.destroy)).subscribe({
        next: ([element, referral]) => {
          if(element?.variables) this.elementVariables.set(element.variables);
  
          this.queryParams = {
            'path': encodeURI(this.elementVariables().route?.app_path ?? ""),
            'id': encodeURI(this.elementVariables().route?.app_id ?? ""),
            'url': encodeURI(domain) +'%2Fget-the-app',
            'utm_campaign':'coca_cola_game_2023_landing',
            'utm_source': 'cloudigo',
            'utm_medium':'web',
            'store':'1',
          };
  
          if(referral?.code) {
            this.virtualCode.set(referral.code);
            try {  
              localStorage?.setItem(`${referral_tracking_name}_${referral_code}`, referral.code);
            }catch(e){}

          }
          this.isLoading.set(false);
        },
        error: (error) => {
          error = error.error || error;
          this.toast.error('Failed to load page contents');
          this.isLoading.set(false);
        }
      });
    });


  }
}

@Injectable({
  providedIn: 'root'
})

export class ElementsService {
  http = inject(HttpClient);

  getElementVariables(tracking_name: string): Observable<{ [key: string]: any }> {
    return this.http.get<any>(backend + 'element', { params: {
      identifier: tracking_name
    }});
  }

  getReferralCode(tracking_name: string, referral_id: string): Observable<{ code: string }>{
    return this.http.get<{ code: string }>(backend + 'campaign/generate-code', {
      params: { campaign: tracking_name, referral: referral_id }
    });
  }
}